import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';
import { defaultLanguage, languages } from '../constants/language.constants';

@Injectable({
  providedIn: 'root',
})
export class CrowdinService {
  private translations: { [lang: string]: any } = {};
  private currentLanguage: string = 'en';

  private frUrl = `https://distributions.crowdin.net/${environment.crowdinHashKey}/content/fr.json`;
  private esUrl = `https://distributions.crowdin.net/${environment.crowdinHashKey}/content/es.json`;
  private nlUrl = `https://distributions.crowdin.net/${environment.crowdinHashKey}/content/nl.json`;
  private itUrl = `https://distributions.crowdin.net/${environment.crowdinHashKey}/content/it.json`;
  private jaUrl = `https://distributions.crowdin.net/${environment.crowdinHashKey}/content/ja.json`;
  private ptUrl = `https://distributions.crowdin.net/${environment.crowdinHashKey}/content/pt.json`;
  private deUrl = `https://distributions.crowdin.net/${environment.crowdinHashKey}/content/de.json`;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.loadLocalTranslations('en');
    }
  }

  getLanguageIndexByName(name: string): number {
    return (
      languages.find(
        (l) =>
          l.value.toLowerCase() === (name || defaultLanguage).toLowerCase(),
      )?.index ?? 0
    );
  }

  public getAllTranslations(): { [key: string]: string } {
    return this.translations[this.currentLanguage] || {};
  }

  public getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  private async loadLocalTranslations(languageCode: string) {
    if (languageCode === 'en') {
      try {
        const response = await fetch('/assets/i18n/en.json');
        if (!response.ok) {
          console.error(
            'Failed to load local English translations:',
            response.status,
          );
          return;
        }
        const jsonData = await response.json();
        this.translations['en'] = jsonData;
        this.currentLanguage = 'en';
      } catch (error) {
        console.error('Error parsing local English translations:', error);
      }
    }
  }

  private async loadTranslations(languageCode: string) {
    if (languageCode === 'en') {
      return;
    }

    try {
      if (languageCode === 'fr') {
        const res = await fetch(this.frUrl);
        if (res.ok) {
          const dataArray = await res.json();
          const dataObj = this.transformArrayToObject(dataArray);
          this.translations['fr'] = dataObj;
          this.currentLanguage = 'fr';
        } else {
          console.error(`Failed to fetch French translations: ${res.status}`);
        }
      } else if (languageCode === 'es') {
        const res = await fetch(this.esUrl);
        if (res.ok) {
          const dataArray = await res.json();
          const dataObj = this.transformArrayToObject(dataArray);
          this.translations['es'] = dataObj;
          this.currentLanguage = 'es';
        } else {
          console.error(`Failed to fetch Spanish translations: ${res.status}`);
        }
      } else if (languageCode === 'nl') {
        const res = await fetch(this.nlUrl);
        if (res.ok) {
          const dataArray = await res.json();
          const dataObj = this.transformArrayToObject(dataArray);
          this.translations['nl'] = dataObj;
          this.currentLanguage = 'nl';
        } else {
          console.error(`Failed to fetch Dutch translations: ${res.status}`);
        }
      } else if (languageCode === 'it') {
        const res = await fetch(this.itUrl);
        if (res.ok) {
          const dataArray = await res.json();
          const dataObj = this.transformArrayToObject(dataArray);
          this.translations['it'] = dataObj;
          this.currentLanguage = 'it';
        } else {
          console.error(`Failed to fetch Italian translations: ${res.status}`);
        }
      } else if (languageCode === 'ja') {
        const res = await fetch(this.jaUrl);
        if (res.ok) {
          const dataArray = await res.json();
          const dataObj = this.transformArrayToObject(dataArray);
          this.translations['ja'] = dataObj;
          this.currentLanguage = 'ja';
        } else {
          console.error(`Failed to fetch Japanese translations: ${res.status}`);
        }
      } else if (languageCode === 'pt') {
        const res = await fetch(this.ptUrl);
        if (res.ok) {
          const dataArray = await res.json();
          const dataObj = this.transformArrayToObject(dataArray);
          this.translations['pt'] = dataObj;
          this.currentLanguage = 'pt';
        } else {
          console.error(
            `Failed to fetch Portuguese translations: ${res.status}`,
          );
        }
      } else if (languageCode === 'de') {
        const res = await fetch(this.deUrl);
        if (res.ok) {
          const dataArray = await res.json();
          const dataObj = this.transformArrayToObject(dataArray);
          this.translations['de'] = dataObj;
          this.currentLanguage = 'de';
        } else {
          console.error(`Failed to fetch German translations: ${res.status}`);
        }
      } else {
        console.warn(`No CDN URL configured for language: ${languageCode}`);
      }
    } catch (error) {
      console.error(`Error loading translations for ${languageCode}:`, error);
    }
  }

  private transformArrayToObject(arr: any[]): any {
    const result: any = {};
    arr.forEach((item) => {
      if (item.identifier && item.translation) {
        result[item.identifier] = item.translation;
      }
    });
    return result;
  }

  public async changeLanguage(languageCode: string) {
    if (languageCode === 'en') {
      if (this.translations['en']) {
        this.currentLanguage = 'en';
      } else {
        await this.loadLocalTranslations('en');
      }
    } else {
      if (this.translations[languageCode]) {
        this.currentLanguage = languageCode;
      } else {
        await this.loadTranslations(languageCode);
        if (this.translations[languageCode]) {
          this.currentLanguage = languageCode;
        } else if (this.translations['en']) {
          this.currentLanguage = 'en';
        }
      }
    }
  }

  public getTranslation(key: string): string {
    const langTranslations = this.translations[this.currentLanguage] || {};
    return langTranslations[key] || key;
  }
}
